import React, { useState } from "react";
import { toast } from "sonner";
import { approveDoc, currentUserLabel, KnowledgeDoc, rejectDoc, requestChanges, useSupportBotDocs } from "./store";
import { Chip, EmptyState, inputCls, PageHeader, primaryBtn, secondaryBtn, StatusBadge } from "./shared";

type Tab = "pending" | "changes" | "all";
type Panel = "actions" | "details" | "history";

const inTab = (d: KnowledgeDoc, tab: Tab) =>
  tab === "pending" ? d.status === "pending" : tab === "changes" ? d.status === "changes_requested" : true;

// Support Bot > Approvals (CRM Admin). Queue on the left, the selected
// submission in the middle, Approve / Request changes / Reject on the right.
const ApprovalsPage: React.FC = () => {
  const docs = useSupportBotDocs().filter((d) => d.history.some((h) => h.text.startsWith("Submitted")));
  const me = currentUserLabel();
  const [tab, setTab] = useState<Tab>("pending");
  const [panel, setPanel] = useState<Panel>("actions");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [comment, setComment] = useState<{ kind: "changes" | "reject"; text: string } | null>(null);

  const list = docs.filter((d) => inTab(d, tab));
  const selected = list.find((d) => d.id === selectedId) || list[0] || null;
  const reviewable = selected && (selected.status === "pending" || selected.status === "changes_requested");

  const select = (id: string) => {
    setSelectedId(id);
    setComment(null);
    setPanel("actions");
  };

  const handleApprove = () => {
    if (!selected) return;
    approveDoc(selected.id, me);
    toast.success("Approved — the bot will use this after testing", { description: selected.name });
  };

  const handleComment = () => {
    if (!selected || !comment?.text.trim()) return;
    if (comment.kind === "changes") {
      requestChanges(selected.id, me, comment.text.trim());
      toast.warning(`Changes requested on ${selected.name.replace(/\.[a-z0-9]+$/i, "")}`, { description: `${selected.uploadedBy} will see your comment.` });
    } else {
      rejectDoc(selected.id, me, comment.text.trim());
      toast.error(`Rejected ${selected.name}`);
    }
    setComment(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-4 sm:p-6">
      <div className="space-y-6">
        <PageHeader title="Approvals" subtitle="Review documents agents submit before the bot uses them." />

        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 grid grid-cols-1 lg:grid-cols-[300px_1fr_300px] min-h-[520px] overflow-hidden">
          {/* Queue */}
          <div className="border-b lg:border-b-0 lg:border-r border-gray-100 dark:border-gray-800">
            <div className="flex gap-2 p-4">
              {(["pending", "changes", "all"] as Tab[]).map((t) => (
                <Chip key={t} active={tab === t} onClick={() => setTab(t)}>
                  {t === "pending" ? "Pending" : t === "changes" ? "Changes" : "All"}
                </Chip>
              ))}
            </div>
            {list.length === 0 ? (
              <EmptyState text="Nothing waiting for review." />
            ) : (
              list.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => select(d.id)}
                  className={`w-full text-left px-4 py-3 border-t border-gray-50 dark:border-gray-800 border-l-2 transition-colors ${
                    selected?.id === d.id ? "border-l-indigo-600 bg-indigo-50/60 dark:bg-indigo-500/10" : "border-l-transparent hover:bg-gray-50 dark:hover:bg-gray-800"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{d.uploadedBy}</span>
                    <StatusBadge status={d.status} />
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 truncate mt-0.5">{d.name}</p>
                  <p className="text-xs text-gray-400 mt-0.5">1 file(s) · {d.updated}</p>
                </button>
              ))
            )}
          </div>

          {/* Selected submission */}
          <div className="p-5 border-b lg:border-b-0 lg:border-r border-gray-100 dark:border-gray-800">
            {!selected ? (
              <EmptyState text="Select a submission to review it." />
            ) : (
              <>
                <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{selected.name}</h2>
                <p className="text-xs text-gray-400 mt-0.5">
                  Submitted by {selected.uploadedBy} · {selected.updated}
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {[selected.product, selected.type, `Visible to ${selected.visibleTo.join(", ")}`].map((t) => (
                    <span key={t} className="px-2.5 py-1 rounded-md bg-gray-50 dark:bg-gray-800 text-xs font-medium text-gray-600 dark:text-gray-300">
                      {t}
                    </span>
                  ))}
                </div>
                {selected.note && (
                  <>
                    <p className="text-[11px] font-semibold tracking-wide text-gray-400 uppercase mt-5 mb-2">Note from submitter</p>
                    <p className="text-sm text-gray-700 dark:text-gray-200">{selected.note}</p>
                  </>
                )}
                <p className="text-[11px] font-semibold tracking-wide text-gray-400 uppercase mt-5 mb-2">Extracted text preview</p>
                <p className="rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/60 px-4 py-3 text-sm text-gray-700 dark:text-gray-200 leading-relaxed">{selected.preview}</p>
              </>
            )}
          </div>

          {/* Actions / Details / History */}
          <div className="p-5">
            {selected && (
              <>
                <div className="flex gap-5 border-b border-gray-100 dark:border-gray-800 mb-4">
                  {(["actions", "details", "history"] as Panel[]).map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPanel(p)}
                      className={`pb-2 text-sm font-semibold capitalize border-b-2 -mb-px transition-colors ${
                        panel === p ? "border-indigo-600 text-indigo-600 dark:text-indigo-400" : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400"
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>

                {panel === "actions" &&
                  (reviewable ? (
                    comment ? (
                      <div className="space-y-2">
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{comment.kind === "changes" ? "What needs changing?" : "Why are you rejecting it?"}</p>
                        <textarea
                          autoFocus
                          rows={4}
                          value={comment.text}
                          onChange={(e) => setComment({ ...comment, text: e.target.value })}
                          placeholder={comment.kind === "changes" ? "e.g. Fix the table formatting in section 2" : "e.g. Duplicates the existing guide"}
                          className={`${inputCls} resize-y`}
                        />
                        <div className="flex gap-2">
                          <button type="button" onClick={() => setComment(null)} className={`${secondaryBtn} flex-1`}>
                            Cancel
                          </button>
                          <button type="button" disabled={!comment.text.trim()} onClick={handleComment} className={`${primaryBtn} flex-1`}>
                            {comment.kind === "changes" ? "Send" : "Reject"}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2.5">
                        <button type="button" onClick={handleApprove} className={`${primaryBtn} w-full`}>
                          Approve
                        </button>
                        <button type="button" onClick={() => setComment({ kind: "changes", text: "" })} className={`${secondaryBtn} w-full`}>
                          Request changes
                        </button>
                        <button
                          type="button"
                          onClick={() => setComment({ kind: "reject", text: "" })}
                          className={`${secondaryBtn} w-full !border-rose-200 !text-rose-600 hover:!bg-rose-50 dark:!border-rose-500/30 dark:!text-rose-400`}
                        >
                          Reject
                        </button>
                      </div>
                    )
                  ) : (
                    <div className="text-sm text-gray-500 dark:text-gray-400 space-y-2">
                      <StatusBadge status={selected.status} />
                      <p>This submission has already been reviewed.</p>
                    </div>
                  ))}

                {panel === "details" && (
                  <dl className="space-y-3 text-sm">
                    {[
                      ["Product", selected.product],
                      ["Type", selected.type],
                      ["Visible to", selected.visibleTo.join(", ")],
                      ["Submitted by", selected.uploadedBy],
                      ["Size", selected.size || "—"],
                    ].map(([k, v]) => (
                      <div key={k} className="flex justify-between gap-3">
                        <dt className="text-gray-500 dark:text-gray-400">{k}</dt>
                        <dd className="font-medium text-gray-900 dark:text-gray-100 text-right">{v}</dd>
                      </div>
                    ))}
                  </dl>
                )}

                {panel === "history" && (
                  <ol className="space-y-3">
                    {[...selected.history].reverse().map((h, i) => (
                      <li key={i} className="text-sm">
                        <p className="text-gray-800 dark:text-gray-100">{h.text}</p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {h.by} · {h.at}
                        </p>
                      </li>
                    ))}
                  </ol>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApprovalsPage;
